import React, { type DetailedHTMLProps, type ButtonHTMLAttributes, cloneElement, forwardRef, useImperativeHandle, type JSX } from "react";
import { twMerge } from "tailwind-merge";

import { useTextAligner } from "../hooks/useTextAligner";

export interface BaseButtonProps extends DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> {
  secondary?: boolean;
  textCenter?: boolean;
  icon: JSX.Element;
}

export interface BaseButtonInstance {
  getWidth: () => number;
}

const BASE_BUTTON_OFFSET = "--base-button-offset";
const PADDING_X = 16;

const STYLES = {
  primary: "bg-neutral-900 text-neutral-25 enabled:hover:bg-neutral-925 disabled:bg-neutral-50 ",
  secondary: "bg-neutral-25 text-neutral-800 border border-neutral-600 enabled:hover:text-neutral-900 enabled:hover:border-neutral-700 disabled:border-neutral-25",
} as const;

export const BaseButton = forwardRef<BaseButtonInstance, BaseButtonProps>(({ disabled, children, secondary = false, textCenter = false, icon, className, ...rest }, ref) => {
  const { buttonProps, contentProps } = useTextAligner(textCenter, BASE_BUTTON_OFFSET);

  useImperativeHandle(
    ref,
    () => ({
      getWidth: () => {
        if (!contentProps.ref.current?.offsetWidth) {
          return 0;
        }

        return contentProps.ref.current.offsetWidth + PADDING_X;
      },
    }),
    []
  );

  return (
    <button
      {...buttonProps}
      {...rest}
      disabled={disabled}
      className={twMerge(
        "group inline-flex h-10 p-2 items-center justify-start font-mono tracking-normal uppercase whitespace-nowrap transition-all duration-300 disabled:text-neutral-500",
        secondary ? STYLES.secondary : STYLES.primary,
        className
      )}
    >
      <span {...contentProps} className="inline-flex gap-2 items-center transition-transform duration-300">
        {cloneElement(icon, {
          className: "group-disabled:text-neutral-500",
        })}

        {children}
      </span>
    </button>
  );
});

BaseButton.displayName = "BaseButton";
